import { z } from 'zod';

import {
  formatErrorWithRequestId,
  getRequestTransportErrorMessage,
  getSafeServerErrorMessage,
  readApiErrorEnvelope,
} from '#/utils/requestError';

/** tab 內容卡片；@container/sheet 讓欄位 grid 跟表單頁一樣依寬度分欄，但不撐滿整頁高度。 */
export const SN_WARRANTY_SURFACE_CLASS = (
  '@container/sheet w-full rounded-lg border border-border bg-background p-6'
);

export const WARRANTY_POLICY_OPTIONS = [
  { value: 'THREE_YEARS', label: '三年' },
  { value: 'FIVE_YEARS', label: '五年' },
  { value: 'CUSTOM', label: '自訂到期日' },
  { value: 'NONE', label: '無保固' },
];

const WARRANTY_POLICY_LABELS = Object.fromEntries(
  WARRANTY_POLICY_OPTIONS.map((option) => [option.value, option.label]),
);

const WARRANTY_SOURCE_LABELS = {
  SALES_ORDER: '銷貨單',
  DIRECT_SALE: '直接銷售',
};

const SN_STATUS_LABELS = {
  REGISTERED: '已登錄',
  RESERVED: '已占用',
  SOLD: '已售出',
  VOID: '已作廢',
};

export function formatWarrantyPolicy(policy) {
  if (!policy) {
    return '—';
  }
  return WARRANTY_POLICY_LABELS[policy] ?? policy;
}

export function formatWarrantySource(source) {
  if (!source?.type) {
    return '—';
  }
  return WARRANTY_SOURCE_LABELS[source.type] ?? source.type;
}

export function formatSnStatus(status) {
  if (!status) {
    return '—';
  }
  return SN_STATUS_LABELS[status] ?? status;
}

const requiredText = (message) => z.string().trim().min(1, message);

function refineCustomExpiry(values, ctx) {
  if (values.warrantyPolicy === 'CUSTOM' && !values.warrantyExpiryDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '自訂政策須填保固迄日',
      path: ['warrantyExpiryDate'],
    });
  }
}

/** 序號不 trim（後端拒絕前後空白），只檢查非空。 */
export const directSaleFormSchema = z.object({
  serialNumber: z.string().min(1, '請輸入完整序號'),
  customerId: requiredText('請選擇客戶'),
  salesDate: requiredText('請選擇銷售日'),
  warrantyPolicy: requiredText('請選擇保固政策'),
  warrantyExpiryDate: z.string().optional().nullable(),
}).superRefine(refineCustomExpiry);

export const DIRECT_SALE_FORM_DEFAULT_VALUES = {
  serialNumber: '',
  customerId: '',
  salesDate: '',
  warrantyPolicy: 'THREE_YEARS',
  warrantyExpiryDate: '',
};

export const warrantyCorrectionFormSchema = z.object({
  salesDate: requiredText('請選擇銷售日'),
  warrantyPolicy: requiredText('請選擇保固政策'),
  warrantyExpiryDate: z.string().optional().nullable(),
  reason: requiredText('請填寫更正原因'),
}).superRefine(refineCustomExpiry);

/** 更正區以目前保固帶入；原因每次重填。 */
export function mapWarrantyToCorrectionForm(warranty) {
  return {
    salesDate: warranty?.salesDate ?? '',
    warrantyPolicy: warranty?.warrantyPolicy ?? 'THREE_YEARS',
    warrantyExpiryDate: warranty?.warrantyPolicy === 'CUSTOM'
      ? (warranty?.warrantyExpiryDate ?? '')
      : '',
    reason: '',
  };
}

/**
 * THREE／FIVE 年由後端依銷售日計算，到期日送 null。
 * CUSTOM 必須有到期日；NONE 必須是 null。
 */
export function buildDirectSalePayload({
  serialNumber,
  customerId,
  salesDate,
  warrantyPolicy,
  warrantyExpiryDate,
}) {
  const expiry = warrantyPolicy === 'CUSTOM' ? (warrantyExpiryDate || null) : null;
  return {
    serialNumber,
    customerId,
    salesDate,
    warrantyPolicy,
    warrantyExpiryDate: expiry,
  };
}

/** 只更正日期、政策、到期日與原因，不改客戶、來源或登錄世代。 */
export function buildWarrantyPatchPayload({
  updatedAt,
  salesDate,
  warrantyPolicy,
  warrantyExpiryDate,
  reason,
}) {
  const expiry = warrantyPolicy === 'CUSTOM' ? (warrantyExpiryDate || null) : null;
  return {
    updatedAt,
    salesDate,
    warrantyPolicy,
    warrantyExpiryDate: expiry,
    reason: reason?.trim() ?? '',
  };
}

const SN_SALES_WARRANTY_ERROR_MESSAGES = {
  SN_SALE_WARRANTY_CUSTOMER_MISMATCH: '客戶與序號登錄的客戶不同',
  SN_SALE_WARRANTY_CONFLICT: '序號已被占用、已售出或重複登錄',
  SN_SALE_WARRANTY_INSPECTION_NOT_PASS: '序號最新檢測未通過，請重新檢測',
  SN_SALE_WARRANTY_MAPPING_GUARDED: '序號對照仍有未售序號，不能變更',
  RESOURCE_VERSION_CONFLICT: '保固資料已被他人更正，已重新載入，請再確認一次',
};

export function getSnSalesWarrantyErrorMessage(error, fallback = '操作失敗') {
  const transportMessage = getRequestTransportErrorMessage(error);
  if (transportMessage) {
    return transportMessage;
  }

  const envelope = readApiErrorEnvelope(error);
  const status = error?.response?.status;
  const mapped = SN_SALES_WARRANTY_ERROR_MESSAGES[envelope?.code];
  if (mapped) {
    return formatErrorWithRequestId(mapped, envelope?.requestId);
  }
  if (status === 403) {
    return '沒有權限執行此操作';
  }
  if (status === 404) {
    return envelope?.message || '找不到資料';
  }
  const serverMessage = getSafeServerErrorMessage(error, fallback);
  if (serverMessage) {
    return serverMessage;
  }
  if (envelope?.message) {
    return envelope.message;
  }
  return envelope?.code ? `${fallback}（${envelope.code}）` : fallback;
}

export function isSnSalesWarrantyVersionConflict(error) {
  return readApiErrorEnvelope(error)?.code === 'RESOURCE_VERSION_CONFLICT';
}

/** history 欄位名稱後端只定語意，這裡容忍 camel 化後的幾種寫法。 */
export function normalizeWarrantyHistory(history) {
  return (history ?? []).map((entry, index) => ({
    key: entry.revision ?? entry.id ?? index,
    revision: entry.revision ?? index + 1,
    previousSalesDate: entry.previousSalesDate ?? null,
    salesDate: entry.salesDate ?? entry.newSalesDate ?? null,
    previousWarrantyPolicy: entry.previousWarrantyPolicy ?? null,
    warrantyPolicy: entry.warrantyPolicy ?? entry.newWarrantyPolicy ?? null,
    previousWarrantyExpiryDate: entry.previousWarrantyExpiryDate ?? null,
    warrantyExpiryDate: entry.warrantyExpiryDate ?? entry.newWarrantyExpiryDate ?? null,
    reason: entry.reason ?? '',
    actorName: entry.actor?.name ?? entry.operator?.name ?? entry.createdBy?.name
      ?? entry.actorName ?? entry.operatorName ?? '',
    changedAt: entry.changedAt ?? entry.createdAt ?? entry.updatedAt ?? null,
  }));
}
